import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Link as MuiLink, Stack, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';
import { DeploymentsIcon } from '../global/CustomIcons';

import { fetchRequest as fetchDeployments } from '../../redux/actions/deployments/list';
import { deleteRequest as deleteDeployment } from '../../redux/actions/deployments/delete';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';

const TERMINAL_DB_STATUSES = new Set(['failed', 'inactive', 'archived']);

// Effective status: same precedence as the BE's resolve_effective_status.
// Terminal Mongo states ALWAYS win over the cluster's live status.
const resolveStatus = (d) => {
  const dbStatus = d?.status;
  const liveStatus = d?.live_status;
  const ready = !!d?.ready;
  if (dbStatus && TERMINAL_DB_STATUSES.has(String(dbStatus).toLowerCase())) {
    return String(dbStatus).toLowerCase();
  }
  if (liveStatus === 'ready' && ready) return 'ready';
  if (['deploying', 'initializing', 'starting'].includes(liveStatus)) return liveStatus;
  if (['deploying', 'initializing', 'starting'].includes(dbStatus)) return dbStatus;
  if (liveStatus === 'failed') return 'failed';
  if (ready) return 'ready';
  return 'unknown';
};

const STATUS_TONE = {
  ready: 'success',
  deploying: 'info',
  initializing: 'info',
  starting: 'info',
  failed: 'error',
  inactive: 'neutral',
  archived: 'neutral',
  unknown: 'neutral',
};

const STATUS_LABELS = {
  ready: 'Ready',
  deploying: 'Deploying',
  initializing: 'Initializing',
  starting: 'Starting',
  failed: 'Failed',
  inactive: 'Inactive',
  archived: 'Archived',
  unknown: 'Unknown',
};

// Labels carry the billing bucket: foundation + external-FM rows are
// served by the shared multi-tenant pools and count against the
// "shared deployments" cap; only KServe pods count as dedicated.
const formatDeploymentType = (t) => {
  switch (t) {
    case 'shared':
    case 'modelmesh_shared':   return 'Shared';
    case 'dedicated':
    case 'kserve_dedicated':   return 'Dedicated';
    case 'foundation':         return 'Foundation · shared';
    case 'external_fm':        return 'External FM · shared';
    default:                   return t || '—';
  }
};

const safeFormatDate = (v, fmt = 'MMM d, h:mm a', fb = 'Never') => {
  if (!v) return fb;
  try { return format(new Date(v), fmt); } catch (_e) { return fb; }
};

const safeRelative = (v) => {
  try { if (!v) return null; return `${formatDistanceToNow(new Date(v))} ago`; }
  catch (_e) { return null; }
};

const DeploymentsPage = ({
  fetchDeployments, deleteDeployment, response, isLoading, error, deleteSuccess,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState([]);
  const [search, setSearch] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deploymentToDelete, setDeploymentToDelete] = useState(null);

  const [localDeployments, setLocalDeployments] = useState([]);
  const isFirstRenderRef = useRef(true);
  const isRefreshingRef = useRef(false);
  const prevDeploymentsRef = useRef(null);

  const total = response?.total || 0;

  // Mirror redux into local state for flicker-free live (WebSocket) refresh.
  useEffect(() => {
    const list = response?.deployments || [];
    if (list && (!prevDeploymentsRef.current
        || JSON.stringify(prevDeploymentsRef.current) !== JSON.stringify(list))) {
      setLocalDeployments(list);
      prevDeploymentsRef.current = list;
    }
  }, [response]);

  // The backend list endpoint accepts a single ``status`` value. When the
  // user selects more than one chip we forward the FIRST and intersect the
  // others client-side.
  useEffect(() => {
    fetchDeployments({
      page: page + 1,
      limit: rowsPerPage,
      status: statusFilter[0] || '',
    });
    isFirstRenderRef.current = false;
  }, [fetchDeployments, page, rowsPerPage, statusFilter]);

  const silentRefresh = useCallback(() => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      fetchDeployments({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter[0] || '',
      });
    } catch (e) {
      console.error('Silent refresh error:', e);
    } finally {
      setTimeout(() => { isRefreshingRef.current = false; }, 1000);
    }
  }, [fetchDeployments, page, rowsPerPage, statusFilter]);

  // Live (no polling): deployments change status server-side
  // (initializing→deploying→deployed/ready/failed). Refresh on any
  // `deployment.*` workspace event; reconnect (msg=null) reconciles too.
  useRealtimeRefresh(eventPrefix('deployment.'), silentRefresh);

  useEffect(() => {
    if (deleteSuccess) {
      fetchDeployments({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter[0] || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  useEffect(() => { setPage(0); }, [statusFilter, search]);

  const handleExploreDeployment = useCallback(
    (d) => navigate(`/deployment/${d.promotion_id}`), [navigate]
  );
  const handleDelete = useCallback((d) => {
    setDeploymentToDelete(d);
    setDeleteDialogOpen(true);
  }, []);
  const confirmDelete = () => {
    if (deploymentToDelete) {
      deleteDeployment({ promotion_id: deploymentToDelete.promotion_id });
      setDeleteDialogOpen(false);
      setDeploymentToDelete(null);
    }
  };
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return localDeployments.filter((d) => {
      // Multi-select status: computed against the resolved (effective)
      // status so chip filters are intuitive.
      if (statusFilter.length > 0) {
        const resolved = resolveStatus(d);
        if (!statusFilter.includes(resolved)) return false;
      }
      if (!q) return true;
      return (d.model_name || '').toLowerCase().includes(q)
        || (d.model_id || '').toLowerCase().includes(q)
        || (d.training_id || '').toLowerCase().includes(q)
        || (d.endpoint || '').toLowerCase().includes(q);
    });
  }, [localDeployments, search, statusFilter]);

  const toggleStatus = (s) =>
    setStatusFilter((prev) => prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]);

  const activeFilterCount =
    (search ? 1 : 0)
    + (statusFilter.length > 0 ? 1 : 0);

  const clearAll = () => {
    setSearch('');
    setStatusFilter([]);
  };

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (d) => {
        const s = resolveStatus(d);
        return (
          <EntityCell
            id={d.promotion_id}
            tone={STATUS_TONE[s] || 'neutral'}
            label={STATUS_LABELS[s] || s}
          />
        );
      },
    },
    {
      key: 'source',
      label: 'Source',
      width: '28%',
      render: (d) => (
        d.is_foundation_direct ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HubIcon sx={{ fontSize: 13, color: theme.palette.info.main }} />
              <Typography variant="body2" sx={{
                fontWeight: 600, fontSize: 13,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: 320,
              }} title={d.model_name}>
                {d.model_name || '—'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              Foundation · zero-shot{d.foundation_adapter_id ? ' (with adapter)' : ''}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CloudSyncIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
              <MuiLink
                component="button" type="button" underline="hover"
                onClick={(e) => { e.stopPropagation(); navigate(`/pipeline/${d.model_id}`); }}
                sx={{
                  fontSize: 13, fontWeight: 600,
                  color: theme.palette.text.primary,
                  textAlign: 'left',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  maxWidth: 320,
                  '&:hover': { color: theme.palette.primary.main },
                }}
              >
                {d.model_name || d.model_id}
              </MuiLink>
            </Box>
            {d.training_id && (
              <Typography variant="caption" color="text.secondary" sx={{
                display: 'block', fontSize: 11,
                fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace',
              }}>
                t/{String(d.training_id).slice(-8)}
              </Typography>
            )}
          </Box>
        )
      ),
    },
    {
      key: 'type',
      label: 'Type',
      width: 170,
      render: (d) => (
        <Typography variant="body2" sx={{ fontSize: 13 }} noWrap title={formatDeploymentType(d.deployment_type)}>
          {formatDeploymentType(d.deployment_type)}
        </Typography>
      ),
    },
    {
      key: 'inferences',
      label: 'Inferences',
      width: 100,
      align: 'right',
      render: (d) => (
        <Typography variant="body2" sx={{
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
          color: (d.inference_count || 0) > 0 ? theme.palette.text.primary : theme.palette.text.disabled,
          fontWeight: (d.inference_count || 0) > 0 ? 600 : 400,
        }}>
          {(d.inference_count || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'deployed_at',
      label: 'Deployed',
      width: 140,
      render: (d) => (
        <Tooltip title={safeFormatDate(d.deployed_at, 'PPpp', 'Never')} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {safeFormatDate(d.deployed_at, 'MMM d, h:mm a', 'Never')}
            </Typography>
            {d.deployed_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {safeRelative(d.deployed_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 80,
      align: 'right',
      stopRowClick: true,
      render: (d) => (
        <RowActions
          onOpen={() => handleExploreDeployment(d)}
          openLabel="Open deployment"
          items={[
            {
              label: 'Delete deployment',
              icon: DeleteOutlineIcon,
              danger: true,
              onClick: () => handleDelete(d),
            },
          ]}
        />
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[{ text: 'Deployments', icon: <DeploymentsIcon /> }]} sx={{ mb: 2 }} />
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, borderRadius: 1 }}>
          <Typography variant="h6">Error Loading Deployments</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Box>
      </Box>
    );
  }

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search by model, training, endpoint…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 240, flex: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Status
        </Typography>
        {[
          { value: 'ready',     label: 'Ready',     color: 'success' },
          { value: 'deploying', label: 'Deploying', color: 'info'    },
          { value: 'failed',    label: 'Failed',    color: 'error'   },
          { value: 'inactive',  label: 'Inactive',  color: 'default' },
        ].map((opt) => {
          const selected = statusFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleStatus(opt.value)}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Deployments', icon: <DeploymentsIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Models promoted to serve live inferences. Updates live as statuses change.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
      />

      <DataTable
        columns={columns}
        rows={filteredRows}
        getRowKey={(row) => row.promotion_id}
        onRowClick={(row) => handleExploreDeployment(row)}
        loading={isLoading && isFirstRenderRef.current}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        emptyState={{
          icon: <DeploymentsIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No deployments match your filters' : 'No Deployments Yet',
          message: activeFilterCount > 0
            ? 'Try a different search or clear the filters.'
            : 'No deployments yet. Promote a successful training to start serving inferences.',
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this deployment? This action cannot be undone.
            {deploymentToDelete && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Model: {deploymentToDelete.model_name || deploymentToDelete.model_id}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  response: state.deployments_list?.response || {},
  isLoading: state.deployments_list?.isLoading || false,
  error: state.deployments_list?.error,
  deleteSuccess: state.deployment_delete?.success || false,
});

const mapDispatchToProps = (dispatch) => ({
  fetchDeployments: (payload) => dispatch(fetchDeployments(payload)),
  deleteDeployment: (payload) => dispatch(deleteDeployment(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentsPage);
